import React from 'react';
import { useTheme } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DSBox from '../../../design-system/DSBox';
import DSIconButton from '../../../design-system/DSIconButton';
import DSSecondaryButton from '../../../design-system/DSSecondaryButton';
import CountryDropdown from '../../../components/CountryDropdown/components/CountryDropdown';
import StateDropdown from '../../../components/StateDropdown/components/StateDropdown';
import DSHeaderTypography from '../../../design-system/Typography/DSHeaderTypography';
import { ReactComponent as CalendarIcon } from '../../../design-system/Icons/CalendarIcon.svg';
import { ReactComponent as MenuIcon } from '../../../design-system/Icons/MenuIcon.svg';
import { CalendarViewType } from '../../../utils/calendarEvents';

export enum NavigationOptions {
  PREV = 'PREV',
  NEXT = 'NEXT',
  TODAY = 'TODAY',
}

type CalendarToolbarProps = {
  label: string;
  onNavigate: (action: NavigationOptions) => void;
  currentDate: Date;
  selectedCountry: string;
  selectedState: string;
  onCountrySelect: (country: string) => void;
  onStateSelect: (state: string) => void;
  currentView: CalendarViewType;
  onViewChange: (view: CalendarViewType) => void;
  disabled?: boolean;
};

const CalendarToolbar = ({
  label,
  onNavigate,
  currentDate,
  selectedCountry,
  selectedState,
  onCountrySelect,
  onStateSelect,
  currentView,
  onViewChange,
  disabled,
}: CalendarToolbarProps) => {
  const { spacing, palette } = useTheme();
  const now = new Date();
  const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const calendarMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1,
  );

  const canGoBack = calendarMonth > currentMonth;

  const handleTodayClick = () => {
    onNavigate(NavigationOptions.TODAY);
  };

  const displayLabel =
    currentView === CalendarViewType.LIST
      ? `${currentDate.getFullYear()} Yearly Events`
      : label;

  return (
    <DSBox
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: spacing(2),
      }}
    >
      <DSBox
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: spacing(1.5),
        }}
      >
        <DSHeaderTypography>{displayLabel}</DSHeaderTypography>

        {currentView === CalendarViewType.GRID && (
          <>
            <DSIconButton
              onClick={() => onNavigate(NavigationOptions.PREV)}
              disabled={!canGoBack || disabled}
            >
              <ArrowBackIosIcon />
            </DSIconButton>

            <DSSecondaryButton
              variant="contained"
              onClick={handleTodayClick}
              disabled={disabled}
            >
              Today
            </DSSecondaryButton>

            <DSIconButton
              onClick={() => onNavigate(NavigationOptions.NEXT)}
              disabled={disabled}
            >
              <ArrowForwardIosIcon />
            </DSIconButton>
          </>
        )}
      </DSBox>

      <DSBox />

      <DSBox
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: spacing(1),
          width: '50%',
        }}
      >
        <DSBox
          sx={{
            width: '50%',
          }}
        >
          <CountryDropdown
            initialCountryCode={selectedCountry}
            onSelect={onCountrySelect}
          />
        </DSBox>

        <DSBox
          sx={{
            width: '50%',
          }}
        >
          <StateDropdown
            countryCode={selectedCountry}
            initialStateCode={selectedState}
            onSelect={onStateSelect}
          />
        </DSBox>

        <DSIconButton
          disabled={disabled}
          onClick={() => onViewChange(CalendarViewType.LIST)}
          sx={{
            backgroundColor:
              currentView === CalendarViewType.LIST
                ? palette.action.selected
                : 'transparent',
            padding: spacing(0.75),
            minWidth: 'auto',
            '&:hover': {
              backgroundColor: palette.action.hover,
            },
          }}
        >
          <MenuIcon />
        </DSIconButton>

        <DSIconButton
          disabled={disabled}
          onClick={() => onViewChange(CalendarViewType.GRID)}
          sx={{
            backgroundColor:
              currentView === CalendarViewType.GRID
                ? palette.action.selected
                : 'transparent',
            padding: spacing(0.75),
            minWidth: 'auto',
            '&:hover': {
              backgroundColor: palette.action.hover,
            },
          }}
        >
          <CalendarIcon />
        </DSIconButton>
      </DSBox>
    </DSBox>
  );
};

export default CalendarToolbar;
